/**
 * SEO HEADING STRUCTURE:
 * TITLE: Study With Strangers | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Study with strangers online. Research-backed benefits of social studying, global community rooms, and collective focus. Join public study rooms free.
 * H1: Study With Strangers: Harnessing Collective Focus for Better Learning
 * H2: Why Study With Others?, The Science of Social Studying, Choose Your Vibe, Virtual Study Rooms and Tools, What We're Building: CSW as the Ultimate Hub
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Globe, Zap, Shield, TrendingUp, Play, ArrowRight, Users, Heart, Lightbulb,
  BookOpen, Target, Coffee, Briefcase, GraduationCap, Timer, Layout, Calendar,
  Brain, Search, MessageSquare, Video, FileText, Lock, Sparkles
} from "lucide-react";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const StudyWithStrangers = () => {
  const quickFeatures = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Global Community",
      description: "Join thousands of students studying around the world",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instant Join",
      description: "No invites needed—jump into any public room instantly",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safe Environment",
      description: "Moderated spaces designed for respectful, focused studying",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Stay Motivated",
      description: "See others working hard and feel inspired to do your best",
    },
  ];

  const vibes = [
    { 
      title: "Chill Mode", 
      icon: <Coffee className="w-10 h-10" />,
      color: "hsl(328 85% 55%)",
      subtitle: "Low-pressure environment for light work", 
      desc: "A relaxed space perfect for reading, brainstorming, or creative work that doesn't require intense focus." 
    },
    { 
      title: "Study Mode", 
      icon: <BookOpen className="w-10 h-10" />,
      color: "hsl(193 95% 50%)",
      subtitle: "Focused atmosphere for serious work", 
      desc: "Ideal for assignments, exam prep, and deep learning. Here everyone is in the zone, helping each other stay on task." 
    },
    { 
      title: "Work Mode", 
      icon: <Briefcase className="w-10 h-10" />,
      color: "hsl(38 92% 50%)",
      subtitle: "High-intensity productivity zone", 
      desc: "For professionals and students tackling challenging projects under tight deadlines." 
    },
  ];

  const toolFeatures = [
    {
      icon: <Timer className="w-6 h-6" />,
      title: "Focus Rooms with Backgrounds and Timers",
      description: "Customise atmospheric backgrounds (quiet library or coffee-shop vibes) and use built-in personal timers to structure your sessions. These cues support Pomodoro-style workflows that research has shown to improve efficiency and mood.",
    },
    {
      icon: <Layout className="w-6 h-6" />,
      title: "Kanban Boards and Dashboards",
      description: "Create shared kanban boards, assign tasks, deadlines and reminders. Dashboards track your progress and help you see the big picture of your assignments.",
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Calendar Integration",
      description: "Calendar integration ensures deadlines don't sneak up on you, and built-in reminders keep your team on the same page.",
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI and Automation",
      description: "AI can summarise discussion threads, prioritise tasks and suggest optimal time blocks. A unified search function lets you find files, notes and chat history instantly.",
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Collaboration Features",
      description: "Collaborate on projects with shared documents, live chat and voice/video calls. These tools support synchronous and asynchronous work.",
    },
  ];

  const cswFeatures = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Unified Workspace",
      description: "Create project-based workspaces with task boards, docs, file storage and calendars. Each workspace has custom access controls and version history.",
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Assistance",
      description: "Use AI to prioritise your to-dos, generate summaries of meeting notes and automate repetitive tasks. AI search across your documents helps you find information quickly.",
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "No Limits, No Price Tag",
      description: "CSW offers unlimited rooms, boards and file storage at no cost. Designed for students, remote teams and freelancers alike.",
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Built-in Security",
      description: "Enterprise-grade encryption keeps your data secure. Tailor workspaces with templates, knowledge wikis and creative backgrounds.",
    },
  ];

  const faqs = [
    {
      question: "Is Study With Strangers free to use?",
      answer: "Yes! CSW offers free, unlimited access to public study rooms with no restrictions on time or usage."
    },
    {
      question: "Do I need to turn my camera on?",
      answer: "Camera usage is optional but encouraged. Research shows that seeing your peers improves engagement and connection in online study sessions."
    },
    {
      question: "How are the rooms moderated?",
      answer: "Our community guidelines ensure respectful, focused studying. Rooms are monitored to maintain a safe and productive environment for all users."
    },
    {
      question: "Can I study with people in different time zones?",
      answer: "Absolutely! Our global community means you can find study partners 24/7, whether you're a night owl in Montreal or an early bird in Shanghai."
    },
    {
      question: "What subjects can I study?",
      answer: "Any subject! Our diverse community includes students from all disciplines—from STEM to humanities, exam prep to creative projects."
    },
  ];

  // JSON-LD Schema
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://csw.live/study-with-strangers/",
        "url": "https://csw.live/study-with-strangers/",
        "name": "Study With Strangers: Harnessing Collective Focus for Better Learning",
        "description": "Study with strangers online. Research-backed benefits of social studying, global community rooms, and collective focus. Join public study rooms free.",
        "inLanguage": "en",
        "dateModified": "2025-01-27",
        "publisher": {
          "@type": "Organization",
          "name": "CSW",
          "url": "https://csw.live"
        },
        "mainEntity": {
          "@type": "SoftwareApplication",
          "name": "CSW Study With Strangers",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "ratingCount": "3000",
            "bestRating": "5",
            "worstRating": "1"
          },
          "featureList": [
            "Public study rooms",
            "Global community",
            "Social accountability",
            "Pomodoro timer",
            "Kanban boards",
            "AI assistance",
            "Calendar integration"
          ]
        },
        "keywords": "study with strangers, public study room, global study community, study online, virtual study room, study with others, collaborative learning, social studying, peer accountability, body doubling, online study group, study stream, focus room",
        "mentions": [
          { "@type": "Thing", "name": "Collaborative Learning" },
          { "@type": "Thing", "name": "Social Accountability" },
          { "@type": "Thing", "name": "Peer Support" },
          { "@type": "Thing", "name": "Pomodoro Technique" },
          { "@type": "Thing", "name": "Body Doubling" },
          { "@type": "Thing", "name": "Group Study" },
          { "@type": "Thing", "name": "Virtual Study Room" },
          { "@type": "Thing", "name": "Online Learning Community" },
          { "@type": "Thing", "name": "Focus Sessions" },
          { "@type": "Thing", "name": "Study Motivation" },
          { "@type": "Thing", "name": "Academic Performance" },
          { "@type": "Thing", "name": "Remote Learning" }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://csw.live/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Study With Strangers",
            "item": "https://csw.live/study-with-strangers/"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Study With Strangers | CSW | Online Workspace | Productivity Tool | Study Stream"
        description="Study with strangers online. Research-backed benefits of social studying, global community rooms, and collective focus. Join public study rooms free."
        keywords="study with strangers, public study room, global study community, study online, virtual study room, study with others, collaborative learning, social studying, peer accountability, body doubling"
        canonicalUrl="/study-with-strangers/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Header />
      <BlogReadingProgress />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">GLOBAL STUDY COMMUNITY</span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-gradient">Study With Strangers:</span>
                <br />
                <span className="text-foreground">Harnessing Collective Focus for Better Learning</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Studying alone can be tough. Distractions creep in, motivation fades and it's easy to feel isolated. 
                Study With Strangers flips the script by letting you tap into a global community of learners.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Join Public Room <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
              
              {/* Video Section */}
              <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full aspect-video object-cover"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                      <Play className="w-5 h-5 text-primary-foreground ml-1" />
                    </div>
                    <span className="text-foreground font-medium">See how it works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Features Grid */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {quickFeatures.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <CardHeader>
                      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {feature.icon}
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Study With Others - Research Section */}
        <section id="why-study-with-others" className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Why Study With <span className="text-gradient">Others?</span>
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto text-lg">
                Research confirms that studying with others—even strangers—dramatically improves focus and outcomes.
              </p>
              
              <div className="space-y-8">
                {/* Global Community */}
                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-4 text-foreground">Global Community and Instant Join</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Joining a public room means you're never studying in a vacuum. Research shows that{" "}
                        <a 
                          href="https://ctl.wustl.edu/resources/benefits-of-group-work/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          cooperative learning and group work boost individual achievement
                        </a>{" "}
                        compared with working alone. When students collaborate, they share knowledge, build on one another's strengths and tackle more complex problems together. 
                        Whether you're a night owl in Montreal or an early bird in Shanghai, you can find a room of fellow learners and feel part of a global{" "}
                        <Link to="/study-stream/" className="text-primary hover:underline">study stream</Link>.
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Safe Spaces */}
                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-4 text-foreground">Safe and Supportive Spaces</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Our rooms are moderated to ensure respectful, focused studying. Research on{" "}
                        <a 
                          href="https://www.uc.edu/news/articles/2024/12/why-is-cooperative-learning-important-in-education.html" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          cooperative and collaborative learning
                        </a>{" "}
                        highlights the importance of clear norms and small group sizes; students working in small groups engage more deeply and are more accountable. 
                        Faculty research indicates that{" "}
                        <a 
                          href="https://www.facultyfocus.com/articles/online-education/the-black-box-problem-why-cameras-matter-in-the-online-classroom/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          seeing your peers improves engagement and connection
                        </a>{" "}
                        in online classes. Learn more about{" "}
                        <Link to="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline">
                          why studying on camera helps
                        </Link>.
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Social Accountability */}
                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-4 text-foreground">Stay Motivated with Social Accountability</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Feeling accountable to others can dramatically raise your chances of finishing a goal. A study cited by{" "}
                        <a 
                          href="https://alumline.source.colostate.edu/being-held-accountable-for-your-goals/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Colorado State University
                        </a>{" "}
                        found that sharing your goals and scheduling accountability appointments can boost success rates from roughly 10% to 95%. 
                        Study With Strangers builds this accountability into every session; when you see others working, you're naturally motivated to keep going.
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Combat Loneliness */}
                <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-4 text-foreground">Combat Loneliness and Discover New Perspectives</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Studying with strangers isn't just about productivity; it also alleviates loneliness. Peer-to-peer groups help students clarify concepts, practice problem-solving and provide emotional support.{" "}
                        <a 
                          href="https://www.thethinkacademy.com/blog/edubriefs-group-learning-vs-individual-learning-which-works-best-for-students/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Group learning fosters collaboration, improved communication and social development
                        </a>. 
                        Meeting students from different disciplines broadens your perspective. Check out our{" "}
                        <Link to="/study-with-friends/" className="text-primary hover:underline">Study With Friends</Link>{" "}
                        page if you prefer studying with people you know.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Science of Social Studying */}
        <section id="science-of-social-studying" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="absolute top-1/4 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                The Science of <span className="text-gradient">Social Studying</span>
              </h2>
              
              {/* Research TL;DR */}
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-12">
                <h3 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Research TL;DR
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>A meta-analysis of 168+ studies found cooperative learners achieve greater knowledge acquisition and retention</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Smaller study groups improve engagement more than larger ones (Frontiers in Psychology)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Accountability appointments boost goal completion from ~10% to 95% (Colorado State University)</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-8">
                {/* Better Learning Outcomes */}
                <article className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-semibold text-foreground flex items-center gap-3">
                    <GraduationCap className="w-7 h-7 text-primary" />
                    Better Learning Outcomes
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Decades of research support the idea that collaborative learning improves academic performance. A{" "}
                    <a 
                      href="https://prepacademytutors.com/the-essential-role-of-study-groups-in-boosting-academic-performance/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      meta-analysis of more than 168 studies
                    </a>{" "}
                    found that students learning cooperatively achieved greater knowledge acquisition, retention and higher-order problem-solving skills than those working alone. 
                    Research from{" "}
                    <a 
                      href="https://ctl.columbia.edu/resources-and-technology/teaching-with-technology/teaching-online/collaborative-learning-online/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Columbia University's Center for Teaching and Learning
                    </a>{" "}
                    notes that collaborative learning increases student engagement, persistence and personal development. 
                    Learn more about the{" "}
                    <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline">
                      science behind "study with me"
                    </Link>.
                  </p>
                </article>

                {/* Peer Support */}
                <article className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-semibold text-foreground flex items-center gap-3">
                    <Users className="w-7 h-7 text-primary" />
                    Peer Support, Engagement and Group Size
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    A recent study in{" "}
                    <a 
                      href="https://pmc.ncbi.nlm.nih.gov/articles/PMC12015942/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Frontiers in Psychology
                    </a>{" "}
                    explored how collaborative learning boosts student engagement. It found that collaborative learning strengthens peer support, and that peer support in turn increases engagement. 
                    The study also observed that smaller groups improve engagement more than larger ones. This reinforces our approach of limiting room sizes and using breakout tables to maintain intimacy and accountability.
                  </p>
                </article>

                {/* Goal Setting */}
                <article className="prose prose-lg max-w-none">
                  <h3 className="text-2xl font-semibold text-foreground flex items-center gap-3">
                    <Target className="w-7 h-7 text-primary" />
                    Goal Setting and Achievement
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Goal tracking isn't just a nice-to-have—it's essential for progress. Researchers have shown that having a clear accountability structure significantly increases the likelihood of completing a goal. 
                    CSW integrates{" "}
                    <Link to="/study-timer/" className="text-primary hover:underline">study timers</Link>, 
                    session goals and productivity statistics so you can set objectives and see your progress. 
                    Our leaderboard and rewards system taps into the positive peer pressure that motivates you to strive for consistency. 
                    Check out our{" "}
                    <Link to="/pomodoro-technique/" className="text-primary hover:underline">Pomodoro technique guide</Link>{" "}
                    to optimise your study sessions.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* Choose Your Vibe */}
        <section id="choose-your-vibe" className="py-24 relative overflow-hidden">
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Choose Your <span className="text-gradient">Vibe</span>
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto text-lg">
                Everyone works differently, so we offer three modes to match your mood. 
                Research on cooperative learning emphasises that clear roles and structures are crucial for effective collaboration.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {vibes.map((vibe, index) => (
                  <Card 
                    key={index} 
                    className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 text-center"
                  >
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-transform"
                      style={{ backgroundColor: `${vibe.color}15`, color: vibe.color }}
                    >
                      {vibe.icon}
                    </div>
                    <h3 className="text-2xl font-bold mb-2" style={{ color: vibe.color }}>{vibe.title}</h3>
                    <p className="text-primary font-medium mb-4">{vibe.subtitle}</p>
                    <p className="text-muted-foreground">{vibe.desc}</p>
                  </Card>
                ))}
              </div>
              
              <p className="text-center text-muted-foreground mt-8">
                Switching between modes is seamless and lets you adapt your environment to your workflow.
              </p>
            </div>
          </div>
        </section>

        {/* Virtual Study Rooms and Tools */}
        <section id="virtual-study-rooms" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Virtual Study Rooms and <span className="text-gradient">Tools</span>
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto text-lg">
                Built-in productivity features to help you work smarter, not harder.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {toolFeatures.map((feature, index) => (
                  <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                        <p className="text-muted-foreground text-sm">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              <p className="text-center text-muted-foreground mt-8">
                These tools support{" "}
                <a 
                  href="https://pubmed.ncbi.nlm.nih.gov/36859717/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Pomodoro-style workflows that research has shown to improve efficiency and mood
                </a>. 
                Explore our{" "}
                <Link to="/task-management/" className="text-primary hover:underline">task management</Link>{" "}
                and{" "}
                <Link to="/online-workspace/" className="text-primary hover:underline">online workspace</Link>{" "}
                features.
              </p>
            </div>
          </div>
        </section>

        {/* What We're Building: CSW */}
        <section id="csw-hub" className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                What We're Building: <span className="text-gradient">CSW as the Ultimate Hub</span>
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-3xl mx-auto text-lg">
                While Study With Strangers connects you with fellow learners, CSW is evolving into an all-in-one hub for productivity. 
                We combine focus tools, task management, collaboration and automation into a single platform.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {cswFeatures.map((feature, index) => (
                  <Card key={index} className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                        <p className="text-muted-foreground text-sm">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-2xl p-8 text-center">
                <p className="text-lg text-muted-foreground">
                  Imagine finishing a group project, planning a presentation and studying for an exam—all within the same environment. 
                  <span className="text-foreground font-medium"> That's the future we're building.</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg px-6"
                  >
                    <AccordionTrigger className="text-left text-lg font-medium hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Join the Global <span className="text-gradient">Study Movement</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Study With Strangers taps into the power of community. Research confirms that studying in groups boosts achievement, engagement and motivation. 
                Join thousands of students studying right now—be part of something bigger.
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Studying Now
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Pages */}
        <section className="py-16 relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Explore More</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                <Link 
                  to="/study-with-me/" 
                  className="p-4 bg-card/50 rounded-lg border border-border/50 hover:border-primary/50 transition-all text-center"
                >
                  <span className="text-foreground font-medium">Study With Me</span>
                </Link>
                <Link 
                  to="/study-with-friends/" 
                  className="p-4 bg-card/50 rounded-lg border border-border/50 hover:border-primary/50 transition-all text-center"
                >
                  <span className="text-foreground font-medium">Study With Friends</span>
                </Link>
                <Link 
                  to="/study-stream/" 
                  className="p-4 bg-card/50 rounded-lg border border-border/50 hover:border-primary/50 transition-all text-center"
                >
                  <span className="text-foreground font-medium">Study Stream</span>
                </Link>
                <Link 
                  to="/study-room/" 
                  className="p-4 bg-card/50 rounded-lg border border-border/50 hover:border-primary/50 transition-all text-center"
                >
                  <span className="text-foreground font-medium">Virtual Study Room</span>
                </Link>
                <Link 
                  to="/pomodoro-technique/" 
                  className="p-4 bg-card/50 rounded-lg border border-border/50 hover:border-primary/50 transition-all text-center"
                >
                  <span className="text-foreground font-medium">Pomodoro Technique</span>
                </Link>
                <Link 
                  to="/online-workspace/" 
                  className="p-4 bg-card/50 rounded-lg border border-border/50 hover:border-primary/50 transition-all text-center"
                >
                  <span className="text-foreground font-medium">Online Workspace</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Missing import fix
const Gift = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="8" width="18" height="4" rx="1"/>
    <path d="M12 8v13"/>
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>
  </svg>
);

export default StudyWithStrangers;
